import React from "react";
import { AbstractDate } from "@linkfluence/models/date";
import {
  DatavizMultipleLineChart,
  DatavizStackedBarChart,
  DatavizStreamChart,
  isMultipleLineChartDataviz,
  isStackedBarChartDataviz,
} from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentDateTerm } from "@radarly/models/search";
import { useFragmentData } from "../data";
import Loading from "../loading";
import MultipleLineChart from "./multiple-line-chart/inner";
import StackedBarChart from "./stacked-bar-chart/inner";
import StreamChart from "./stream-chart/inner";

export interface SharedWidgetsDateTermProps {
  project: number;
  fragment: SearchFragmentDateTerm;
  dataviz:
    | DatavizMultipleLineChart
    | DatavizStackedBarChart
    | DatavizStreamChart;
  facet: FullFacet;
  onClickLegend?: (uid: string | number | Array<string | number>) => void;
  onClickElement?: (
    uid: string | number | Array<string | number>,
    range: { from: Date; to: Date }
  ) => void;
  className?: string;
  forceReload?: boolean;
  disableAnimation?: boolean;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
  closeFullscreen?: () => void;
}

export default function SharedWidgetsDateTerm({
  project,
  fragment,
  dataviz,
  forceReload,
  isFullscreen,
  closeFullscreen,
  onClickElement,
  onClickLegend,
  ...props
}: SharedWidgetsDateTermProps) {
  const { loading, data } = useFragmentData(project, fragment);
  const _onClickElement = (
    uid: string | number | Array<string | number>,
    range: { from: Date; to: Date }
  ) => {
    if (isFullscreen) {
      closeFullscreen();
    }
    onClickElement(uid, range);
  };

  const _onClickLegend = (uid: string | number | Array<string | number>) => {
    if (isFullscreen) {
      closeFullscreen();
    }
    onClickLegend(uid);
  };

  return (
    <Loading loading={loading} forceReload={forceReload}>
      {isMultipleLineChartDataviz(dataviz) ? (
        <MultipleLineChart
          loading={loading}
          data={data}
          fragment={fragment}
          dataviz={dataviz}
          onClickElement={_onClickElement}
          onClickLegend={_onClickLegend}
          isFullscreen={isFullscreen}
          {...props}
        />
      ) : isStackedBarChartDataviz(dataviz) ? (
        <StackedBarChart
          loading={loading}
          data={data}
          fragment={fragment}
          dataviz={dataviz}
          onClickElement={_onClickElement}
          onClickLegend={_onClickLegend}
          isFullscreen={isFullscreen}
          {...props}
        />
      ) : (
        <StreamChart
          loading={loading}
          data={data}
          fragment={fragment}
          dataviz={dataviz}
          onClickElement={_onClickElement}
          onClickLegend={_onClickLegend}
          {...props}
        />
      )}
    </Loading>
  );
}
